/**
 * OfferUp via the Mac agent.
 *
 * Listings are no longer posted by replaying OfferUp's private API with
 * captured credentials. Instead the web app enqueues jobs; a Mac running the
 * agent in tools/offerup-agent claims them and drives the real OfferUp iOS
 * app through iPhone Mirroring. This module is the thin queue client.
 */
import api from "./api";

/** Round a price up to the nearest $5 below $100, nearest $10 above. */
export function roundPrice(price) {
  const n = Number(price);
  if (!Number.isFinite(n) || n <= 0) return n;
  const step = n < 100 ? 5 : 10;
  return Math.ceil(n / step) * step;
}

/**
 * The old credential flow is gone; the agent authenticates with its own
 * FrugalStack login. Pages that used to gate on configuration can always
 * offer OfferUp actions now.
 */
export function isConfigured() {
  return true;
}

async function enqueue(action, entityId, payload, runner) {
  const response = await api.post("/offerup-queue", {
    entity_id: entityId,
    action,
    payload,
    ...(runner ? { runner } : {}),
  });
  return response.data?.data;
}

/**
 * Where to send the browser so the userscript posts a queued job.
 *
 * Same shape as the Vine "Fill review" link: open the marketplace in the
 * seller's own browser with the job id in the URL, and the userscript
 * (tools/amazon-export.user.js) does the rest using their live OfferUp
 * session — no Mac, no phone, no second browser profile.
 */
export function browserPostUrl(jobId) {
  return `https://offerup.com/?fs-post=${encodeURIComponent(jobId)}#fs-post`;
}

/**
 * Queue a listing for creation.
 * @param {Object} product - entity row (id, name, description, condition, prices)
 * @param {Object} options - { title, description, price, condition } overrides
 */
export function queueCreate(product, options = {}) {
  return enqueue(
    "create",
    product.id,
    {
      title: options.title || product.name,
      description: options.description || product.description || product.name,
      price:
        options.price ??
        roundPrice(
          product.list_sale_price ||
            product.min_sale_price ||
            product.retail_price,
        ),
      condition: options.condition || product.condition || "good",
      category: options.category,
    },
    options.runner,
  );
}

/**
 * OfferUp's top-level categories (from the app's "Select a category" sheet).
 * The agent types the value into the sheet's search box, so any subcategory
 * text (e.g. "Skincare") works too.
 */
export const CATEGORIES = [
  "Electronics & Media",
  "Home & Garden",
  "Clothing, Shoes, & Accessories",
  "Baby & Kids",
  "Vehicles",
  "Toys, Games, & Hobbies",
  "Sports & Outdoors",
  "Collectibles & Art",
  "Pet supplies",
  "Health & Beauty",
  "Wedding",
  "Business equipment",
  "Tickets",
];

/** Queue an update for an already-posted listing. */
export function queueUpdate(product, options = {}) {
  return enqueue("update", product.id, {
    title: options.title || product.name,
    description: options.description,
    price: options.price,
    condition: options.condition,
    category: options.category,
    external_id: options.externalId,
    // The agent finds the listing by its currently-posted title, which can
    // differ from the new title when the update renames it.
    find_title: options.findTitle || options.title || product.name,
  });
}

/** Queue removal of a posted listing. */
export function queueDelete(product, options = {}) {
  return enqueue("delete", product.id, {
    title: options.title || product.name,
    external_id: options.externalId,
    // The agent finds the listing by its currently-posted title.
    find_title: options.findTitle || options.title || product.name,
  });
}

/** Recent jobs, optionally filtered by status ('queued'|'claimed'|'done'|'failed'). */
export async function listJobs(status) {
  const response = await api.get("/offerup-queue", {
    params: status ? { status } : {},
  });
  return response.data?.data || [];
}

/** Cancel a still-queued job. */
export async function cancelJob(jobId) {
  const response = await api.delete(`/offerup-queue/${jobId}`);
  return response.data?.data;
}

const offerup = {
  CATEGORIES,
  roundPrice,
  isConfigured,
  queueCreate,
  queueUpdate,
  queueDelete,
  listJobs,
  cancelJob,
};

export default offerup;
